import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Grid,
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import ChevronRight from "@mui/icons-material/ChevronRight";

import appleappstore from "./../Modals/app-store-logo.png";
import googleplaystore from "./../Modals/google-play-logo.png";

import CookieConsent from "react-cookie-consent";

import { isMobile, isTablet } from "react-device-detect";
import { SupportFabButton } from "../Support/SupportFabButton";

/**
 * PM5 T0 pilot — the first component converted from MUI v4 + `makeStyles` to
 * MUI v5+ (`@mui/material`) + `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T0) and docs/mui-conversion-guide.md.
 *
 * Acceptance bar is **visual parity**, not tidiness: every rule below is the
 * v4 rule it replaces, and each place where a v5+ library default would have
 * changed the rendering is compensated for with a comment saying why.
 */

/* ---------------------------------------------------------------------- *
 * styled() — host elements that carry a fixed, reused style
 * ---------------------------------------------------------------------- */

// was: useStyles().footer
const FooterRoot = styled("div")(({ theme }) => ({
  display: "flex",
  backgroundColor: theme.palette.background.ffNavy,
  borderTop: `10px solid ${theme.palette.background.ffNavyLight}`,
  paddingBottom: "20px",
}));

// was: useStyles().appStoreLogo — rendered 4× (two mobile, two desktop), and a
// bare <img> has no `sx` prop, so this is a styled host element rather than a
// shared sx object. The two literal media queries become breakpoint helpers;
// themeV5 pins the v4 breakpoint values, so the emitted CSS text is identical.
const AppStoreLogo = styled("img")(({ theme }) => ({
  width: "90%",
  maxWidth: "140px",
  [theme.breakpoints.up("sm")]: {
    width: "90%",
    maxWidth: "140px",
  },
  [theme.breakpoints.up("md")]: {
    width: "70%",
    maxWidth: "120px",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — one-off / repeated styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().subHeading
const subHeadingSx = {
  color: "primary.main",
  textTransform: "uppercase",
  fontSize: "1rem",
};

// was: useStyles().smlText
const smlTextSx = {
  fontSize: "0.7rem",
  margin: "10px 0",
  color: "common.darkgray",
};

// was: useStyles().inlineList
// The v4 rule also carried a nested `a: { "&:hover": { color: … } }` key. JSS's
// nested plugin only honours keys containing `&` or `@`, so that block has
// never produced any CSS. It is deliberately NOT carried over: emotion *does*
// support bare descendant selectors, so copying it verbatim would introduce a
// hover colour that production has never shown — a visual change, not a port.
const inlineListSx = {
  display: "flex",
  flexDirection: "row",
  padding: 0,
  color: "#eee",
};

// was: useStyles().inlineDisplay — `textDecorationColor` is not an sx style
// prop, so a palette path string would be emitted raw; read it off the theme.
const inlineDisplaySx = (theme) => ({
  display: "inline-block",
  padding: "0",
  "& a": {
    textDecorationColor: theme.palette.common.darkgray,
  },
});

// was: useStyles().iconStyle
const iconSx = {
  fontSize: "2rem",
  padding: "10px",
  backgroundColor: "background.ffNavyLight",
  borderRadius: "50%",
  margin: "10px",
  color: "common.lightgray",
  cursor: "pointer",
  "&:hover": {
    color: "primary.main",
  },
};

// was: useStyles().moreInfoButton
// `color: "text.primary"` is NOT in the v4 rule — it restores a v4 default.
// v4's Button defaulted to `color="default"`, whose root style was
// `color: theme.palette.text.primary`. v5+ deleted the `default` colour and
// made `primary` the default, which here would paint the label lime on the
// lime `typography.button` background — i.e. invisible. Pinning the colour
// keeps the button rendering exactly as it does today.
const moreInfoButtonSx = {
  // `float: "center"` is not valid CSS and never applied; kept verbatim
  // because removing it is a tidy-up, not a conversion.
  float: "center",
  marginTop: "10px",
  paddingLeft: "10px",
  paddingRight: "10px",
  paddingTop: "3px",
  paddingBottom: "3px",
  color: "text.primary",
};

// The v4 file used `<Grid item>` in three places that sit OUTSIDE any
// `<Grid container>`. In v4 that still produced `flex-basis` + `max-width`
// percentages, and `max-width` applies to a block element, so those percentages
// really were constraining the layout. v5+'s Grid computes its width from
// `--Grid-parent-columns` / `--Grid-parent-columnSpacing`, both published by a
// container's `> *` rule — with no container the calc() is invalid and the
// element falls back to full width. So these three become plain Boxes carrying
// the exact v4 declarations rather than a Grid that would silently widen.
const orphanGridItemSx = (xs, sm) => (theme) => ({
  boxSizing: "border-box",
  flexGrow: 0,
  flexBasis: xs,
  maxWidth: xs,
  ...(sm
    ? {
        [theme.breakpoints.up("sm")]: {
          flexBasis: sm,
          maxWidth: sm,
        },
      }
    : {}),
});

export default function Footer() {
  const theme = useTheme();

  // v4's <Hidden> was removed in v5+. Its default implementation was "js",
  // which UNMOUNTS the subtree rather than hiding it with CSS, so the faithful
  // port is useMediaQuery — one call per original element, evaluated with the
  // same polarity (Hidden renders null when its query matches).
  //
  // API delta: v4's `down(key)` meant "up to the START of the NEXT breakpoint",
  // so `xsDown` === `max-width: 599.95px`. v5+ made `down(key)` mean
  // "below `key`", so the same query is now spelled `down("sm")`.
  const hiddenSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const hiddenXsDown = useMediaQuery(theme.breakpoints.down("sm"));

  // #1264: every page's footer badges go straight to their own store. /app's
  // footer used smart.link (#1124), which sent phones to the sign-up page.
  function AppLinks() {
    if (isMobile || isTablet) {
      return (
        <>
          <List dense>
            <ListItem disableGutters>
              <a
                href={
                  "https://apps.apple.com/us/app/farmflix/id1490609587?ls=1"
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppStoreLogo
                  src={appleappstore}
                  alt="Apple App Store Graphic"
                />
              </a>
            </ListItem>
            <ListItem disableGutters>
              <a
                href={
                  "https://play.google.com/store/apps/details?id=com.farmflix&hl=en"
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppStoreLogo
                  src={googleplaystore}
                  alt="Google Play Store Graphic"
                />
              </a>
            </ListItem>
            {/* <ListItem disableGutters>
              <Link to="/farmflix-app" style={{ textDecoration: "none" }}>
                <ListItemText primary="About the App" />
              </Link>
            </ListItem> */}
          </List>
        </>
      );
    } else {
      return (
        <>
          <List dense>
            <ListItem disableGutters>
              <ListItemText primary="Available on:" />
            </ListItem>
            <ListItem disableGutters>
              <AppStoreLogo src={appleappstore} alt="Apple App Store Graphic" />
            </ListItem>
            <ListItem disableGutters>
              <AppStoreLogo
                src={googleplaystore}
                alt="Google Play Store Graphic"
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="Search 'FarmFLiX'" />
            </ListItem>
          </List>
        </>
      );
    }
  }

  return (
    <FooterRoot>
      <Container sx={{ alignSelf: "flex-end" }}>
        <Grid container>
          {/* v5+ Grid: the `item` prop is gone and the breakpoint props are
              collapsed into `size`. With the container's spacing at its default
              of 0 the computed widths are identical to v4's. */}
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Box sx={{ m: 4 }}>
              <Typography variant="h6" sx={subHeadingSx}>
                Get the App
              </Typography>
              <AppLinks />
            </Box>
            <Box sx={{ m: 4 }}>
              <Typography variant="h6" sx={subHeadingSx}>
                #onThePull
              </Typography>
              <Typography variant="body2">
                View information about the event, live leaderboard and more.
              </Typography>

              <Link to="/onthepull">
                <Button sx={moreInfoButtonSx}>
                  More info
                  <ChevronRight />
                </Button>
              </Link>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <Box sx={{ m: 4 }}>
              <Typography variant="h6" sx={subHeadingSx}>
                About Us
              </Typography>
              <List dense>
                <ListItem disableGutters>
                  <Link to="/about" style={{ textDecoration: "none" }}>
                    <ListItemText primary="About FarmFLiX" />
                  </Link>
                </ListItem>
                <ListItem disableGutters>
                  <Link to="/careers" style={{ textDecoration: "none" }}>
                    <ListItemText primary="Careers" />
                  </Link>
                </ListItem>
                <ListItem disableGutters>
                  <a
                    href="https://shop.farmflix.tv"
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{ textDecoration: "none" }}
                  >
                    <ListItemText primary="The Shop" />
                  </a>
                </ListItem>
                <ListItem disableGutters>
                  <Link to="/help" style={{ textDecoration: "none" }}>
                    <ListItemText primary="Help" />
                  </Link>
                </ListItem>
                <ListItem disableGutters>
                  <Link to="/usemycode" style={{ textDecoration: "none" }}>
                    <ListItemText primary="Redeem Giftcard" />
                  </Link>
                </ListItem>
                <ListItem disableGutters>
                  <Link to="/contact" style={{ textDecoration: "none" }}>
                    <ListItemText primary="Contact Us" />
                  </Link>
                </ListItem>
                {/* <ListItem disableGutters>
                  <ListItemText primary="Accounts &amp; Billing" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Plans &amp; Pricing" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Ways to Watch" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="Speed Test" />
                </ListItem> */}
              </List>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 6 }}>
            <Box sx={{ m: 4 }}>
              <Typography variant="h6" sx={subHeadingSx}>
                Find Us On
              </Typography>
              <a
                href={"https://en-gb.facebook.com/farmflix/"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FacebookIcon sx={iconSx} />
              </a>
              <a
                href={"https://www.instagram.com/farmflix/?hl=en"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <InstagramIcon sx={iconSx} />
              </a>
              <a
                href={"https://www.youtube.com/farmflix"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <YouTubeIcon sx={iconSx} />
              </a>
              <Typography variant="body2">
                If you require help/assistance or are a content creator and
                would like to speak to us, please get in touch at{" "}
                <b>info@farmflix.tv</b>
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Divider />
        <Box sx={orphanGridItemSx("100%", "83.333333%")}>
          <Box sx={{ mx: 4, my: 2 }}>
            {!hiddenSmUp && (
              <>
                <Box sx={orphanGridItemSx("66.666667%")}>
                  <List dense sx={inlineListSx}>
                    <ListItem disableGutters sx={inlineDisplaySx}>
                      <ListItemText
                        disableTypography
                        primary={
                          <Link to="/terms" style={{ textDecoration: "none" }}>
                            <Typography sx={smlTextSx}>
                              Terms &amp; Conditions
                            </Typography>
                          </Link>
                        }
                      />
                    </ListItem>
                    {/* <ListItem disableGutters sx={inlineDisplaySx}>
                      <ListItemText
                        disableTypography
                        primary={
                          <Link to="/sitemap" style={{ textDecoration: "none" }}>
                            <Typography sx={smlTextSx}>
                              Sitemap
                            </Typography>
                          </Link>
                        }
                      />
                    </ListItem> */}
                  </List>
                </Box>
                <Box sx={orphanGridItemSx("66.666667%")}>
                  <List dense sx={inlineListSx}>
                    <ListItem disableGutters sx={inlineDisplaySx}>
                      <ListItemText
                        disableTypography
                        primary={
                          <Link
                            to="/privacy-policy"
                            style={{ textDecoration: "none" }}
                          >
                            <Typography sx={smlTextSx}>
                              Privacy Policy
                            </Typography>
                          </Link>
                        }
                      />
                    </ListItem>
                    {/* <ListItem disableGutters sx={inlineDisplaySx}>
                      <ListItemText
                        disableTypography
                        primary={
                          <Link
                            to="/cookiepolicy"
                            style={{ textDecoration: "none" }}
                          >
                            <Typography sx={smlTextSx}>
                              Cookie Policy
                            </Typography>
                          </Link>
                        }
                      />
                    </ListItem> */}
                  </List>
                </Box>
                <Box sx={orphanGridItemSx("100%")}>
                  <List dense sx={inlineListSx}>
                    <ListItem disableGutters sx={inlineDisplaySx}>
                      <ListItemText
                        disableTypography
                        primary={
                          <Typography sx={smlTextSx}>
                            &copy; 2022 FarmFLiX.tv
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </Box>
              </>
            )}
            {!hiddenXsDown && (
              <List dense sx={inlineListSx}>
                <ListItem disableGutters sx={inlineDisplaySx}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography sx={smlTextSx}>
                        &copy; 2022 FarmFLiX.tv
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem disableGutters sx={inlineDisplaySx}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Link to="/terms" style={{ textDecoration: "none" }}>
                        <Typography sx={smlTextSx}>
                          Terms &amp; Conditions
                        </Typography>
                      </Link>
                    }
                  />
                </ListItem>
                <ListItem disableGutters sx={inlineDisplaySx}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Link
                        to="/privacy-policy"
                        style={{ textDecoration: "none" }}
                      >
                        <Typography sx={smlTextSx}>Privacy Policy</Typography>
                      </Link>
                    }
                  />
                </ListItem>
                {/* <ListItem disableGutters sx={inlineDisplaySx}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Link
                        to="/cookiepolicy"
                        style={{ textDecoration: "none" }}
                      >
                        <Typography sx={smlTextSx}>
                          Cookie Policy
                        </Typography>
                      </Link>
                    }
                  />
                </ListItem>
                <ListItem disableGutters sx={inlineDisplaySx}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Link to="/sitemap" style={{ textDecoration: "none" }}>
                        <Typography sx={smlTextSx}>
                          Sitemap
                        </Typography>
                      </Link>
                    }
                  />
                </ListItem> */}
              </List>
            )}
          </Box>
        </Box>
      </Container>
      <CookieConsent
        location="bottom"
        buttonText="Accept Cookies"
        cookieName="farmflix.tv"
        style={{
          background: "#1ab9e8",
          color: "#fff",
          fontSize: "0.9rem",
          zIndex: "1200000",
        }}
        buttonStyle={{
          fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"],
          background: "#2bc4f1",
          fontSize: "0.8rem",
          fontWeight: "500",
          color: "#fff",
          borderRadius: "25px",
          border: "2px solid #13b8ea",
          textTransform: "uppercase",
          padding: "6px 20px",
        }}
        expires={150}
      >
        We use cookies to personalise your experience. By continuing to use
        FarmFLiX.tv, you agree to our use of cookies.
      </CookieConsent>
      <SupportFabButton />
    </FooterRoot>
  );
}
